// frontend/src/components/InvitationCreator.jsx

import { useState } from "react"
import PropTypes from "prop-types"
import { API } from '../config/apiPaths';
import { useTranslation } from "../i18n"
import NotificationChannelSelector from "./NotificationChannelSelector"
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import es from 'react-phone-number-input/locale/es.json'
import en from 'react-phone-number-input/locale/en.json'

export default function InvitationCreator({ onClose, onSave, groupName, ownerName, ownerEmail }) {
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: ""
  })

  const [notifyFlags, setNotifyFlags] = useState(
    { notify_by_email: false, notify_by_whatsapp: false, cc_leader: false }
  )

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [isFormValid, setIsFormValid] = useState(false);

  const { _, lang } = useTranslation()

  const validateForm = (data) => {
    const { contact_name, contact_phone, contact_email } = data;
    if (!contact_name || !contact_email) {
      return false;
    }
    if (contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact_email)) {
      return false;
    }
    if (contact_phone && !isValidPhoneNumber(contact_phone)) {
      return false;
    }
    return true;
  }

  const handlePhoneChange = (value) => {
    const updated = { ...formData, contact_phone: value };
    setFormData(updated);
    setIsFormValid(validateForm(updated));
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    setIsFormValid(validateForm(updated));
  }

  const handleSubmit = async () => {
    if (!isFormValid) {
      setError(_('Please complete all required fields correctly.'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const payload = {
        contact_name: formData.contact_name.toUpperCase(),
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email.toLowerCase(),
        group_name: groupName,
        owner_name: ownerName,
        owner_email: ownerEmail,
        notify_by_email: notifyFlags.notify_by_email,
        notify_by_whatsapp: notifyFlags.notify_by_whatsapp,
        cc_leader: notifyFlags.cc_leader,
      };

      const hashSession =
        JSON.parse(sessionStorage.getItem('hashSession'))?.hash || '';

      const url =
        `${process.env.REACT_APP_API_BASE}${API.invitations.create()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-hash': hashSession,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || _('Failed to create invitation.'));
      }

      // Pasamos "meta" con el resultado de notificación
      onSave(data.invitation, {
        emailSent: !!data.email_sent,
        notifyByEmail: !!payload.notify_by_email,
      });

      onClose();
    } catch (err) {
      setError(err?.message || _('Unknown error.'));
    } finally {
      setLoading(false);
    }
  };

  const phoneInputLabels = lang === 'es' ? es : en;

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2 className="modal-title">{_('Create new invitation')}</h2>

        <div className="form-grid">
          <label className="form-label">
            <span className="label-highlight">{_('Contact name')}</span>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              className="input-inline"
            />
          </label>

          <label className="form-label">
            <span className="label-highlight">{_('Phone')}</span>
            <div className="phone-input-container">
              <PhoneInput
                placeholder={_("Enter phone number")}
                defaultCountry="DO"
                value={formData.contact_phone}
                onChange={handlePhoneChange}
                labels={phoneInputLabels}
                className="phone-input-custom" // estiliza el <input>
                countrySelectProps={{ className: "country-select-dark-colors" }}
              />
            </div>
          </label>

          <label className="form-label">
            <span className="label-highlight">{_('Email')}</span>
            <input
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              className="input-inline"
            />
          </label>

          <NotificationChannelSelector
            contactEmail={formData.contact_email}
            contactPhone={formData.contact_phone}
            defaultEmail={true}
            defaultWhatsapp={false}
            defaultCcLeader={false}
            onChange={setNotifyFlags}
          />

          {error && <p className="form-error">{error}</p>}
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="button-secondary">
            {_('Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="button-primary"
            disabled={loading || !isFormValid}
          >
            {loading ? _('Creating...') : _('Create invitation')}
          </button>
        </div>
      </div>
    </div>
  )
}

InvitationCreator.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
}